import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
// core components
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
// import Table from "components/Table/Table";
import Card from '@/components/Card/Card';
import CardBody from '@/components/Card/CardBody';
import CardHeader from '@/components/Card/CardHeader';
import AddIcon from '@mui/icons-material/Add';
import loader from '../../assets/img/loader.svg';
// components for table.
import TablePaginationActions from '@/views/Managefile/TablePaginationActions/TablePaginationActions';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { getOrganizations, setActiveOrganization } from './Organizations.api';
import { OrganizationsApi } from '@/api/clients/organizations/organizations.api';
import { FACILITY_PORTAL_URL } from '@/config';

export default function Organizations() {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const [organizations, setOrganizations] = useState([]);
  const [errmsg, setErrmsg] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  //   functions for table pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPage = (event) => {
    let rows = parseInt(event.target.value);
    setRowsPerPage(rows);
    setPage(0);
  };

  const handleLoginToOrganization = async (organizationId) => {
    try {
      await setActiveOrganization(organizationId);

      const selectedOrganization = organizations.find(
        (org) => org.id === organizationId,
      );

      if (selectedOrganization) {
        posthog.group('organization', selectedOrganization.id, {
          name: selectedOrganization.name,
        });
      }

      const userId = localStorage.getItem('userId');
      const userEmail = localStorage.getItem('loginEmail');

      if (userId && selectedOrganization) {
        posthog.identify(JSON.parse(userId), {
          name: userEmail,
          email: userEmail,
          organization: selectedOrganization.name,
        });
      }

      posthog.capture('admin login to organization', {
        organizationId: organizationId,
        organizationName: selectedOrganization?.name,
      });

      const organizationFeatures = await OrganizationsApi.getOrganizationFeatures(
        organizationId,
      );
      localStorage.setItem(
        'organizationFeatures',
        JSON.stringify(organizationFeatures),
      );
      localStorage.setItem(
        'organizationName',
        selectedOrganization?.name || '',
      );
      localStorage.setItem('currentOrganizationId', organizationId);

      window.open('/facility/facilitydashboard', '_blank');
    } catch (error) {
      console.error('Error logging into organization:', error);
      console.error('Full error object:', error.response || error);
    }
  };

  const handleLoginToFacilityPortal = async (organizationId) => {
    try {
      const selectedOrganization = organizations.find(
        (org) => org.id === organizationId,
      );

      posthog.capture('admin login to facility portal', {
        organizationId: organizationId,
        organizationName: selectedOrganization?.name,
      });

      const { handoffToken } = await OrganizationsApi.getFacilityPortalHandoff(
        organizationId,
      );

      if (!handoffToken) {
        console.error('Facility portal handoff token missing in response');
        return;
      }

      window.open(
        `${FACILITY_PORTAL_URL}/user/facilitylogin?handoff=${encodeURIComponent(
          handoffToken,
        )}&redirect=/payments`,
        '_blank',
        'noopener,noreferrer',
      );
    } catch (error) {
      console.error('Error opening facility portal:', error);
      console.error('Full error object:', error.response || error);
    }
  };

  useEffect(() => {
    setLoaded(true);
    getOrganizations()
      .then((resp) => {
        if (resp?.data?.errmsg) {
          setErrmsg(resp.data.errmsg);
          setLoaded(false);
          return;
        }

        setOrganizations(resp?.data?.organizations || []);
        setLoaded(false);
      })
      .catch((err) => {
        console.error('Error fetching organizations:', err);
        setOrganizations([]);
        setLoaded(false);
      });
  }, []);

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color='primary'>
            <h4>{t('organizations.title')}</h4>
            {errmsg && <p style={{ color: '#ff0000' }}>{errmsg}</p>}
          </CardHeader>

          <CardBody>
            <div>
              {loaded && (
                <div className='loaders'>
                  <img src={loader} />
                </div>
              )}
              <div className='t-header'>
                <div className='t-header-left'>
                  <Link to={`/admin/createOrganization`} className='link'>
                    <span className='add-icon'>
                      <AddIcon />
                    </span>
                    {t('organizations.createButton')}
                  </Link>
                </div>
                <div className='t-header-right'></div>
              </div>

              <Table width='100%' id='template'>
                <TableHead>
                  <TableRow>
                    <TableCell align='left'>
                      {t('organizations.table.name')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('organizations.table.createdBy')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('organizations.table.createdAt')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('organizations.table.customEmailDomain')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('organizations.table.giftcardUids')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('organizations.table.donationUids')}
                    </TableCell>
                    <TableCell align='left'>
                      {t('organizations.table.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>

                {organizations && organizations.length > 0 ? (
                  <TableBody>
                    {(rowsPerPage > 0
                      ? organizations.slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        )
                      : organizations
                    ).map((obj, index) => (
                      <TableRow className='txtalign' key={index}>
                        <TableCell width='20%'>{obj && obj.name}</TableCell>
                        <TableCell width='20%'>
                          {obj && obj.created_by}
                        </TableCell>
                        <TableCell width='10%'>
                          {obj && obj.created_at}
                        </TableCell>
                        <TableCell width='10%'>{obj && obj.domains}</TableCell>
                        <TableCell width='10%'>
                          {(obj && obj.tango_giftcards_uids?.length) || 0}
                        </TableCell>
                        <TableCell width='10%'>
                          {(obj && obj.tango_donations_uids?.length) || 0}
                        </TableCell>
                        <TableCell width='10%'>
                          {obj && obj.id && (
                            <>
                              <Link
                                to={`/admin/updateOrganization/${obj.id}`}
                                title={t('organizations.updateTitle')}>
                                <span className='material-icons'>edit</span>
                              </Link>
                              <button
                                onClick={() =>
                                  handleLoginToOrganization(obj.id)
                                }
                                title={t('organizations.loginTitle')}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  marginLeft: '8px',
                                }}>
                                <span
                                  className='material-icons'
                                  style={{ color: '#3AB795' }}>
                                  login
                                </span>
                              </button>
                              <button
                                type='button'
                                aria-label={t('organizations.loginPortalTitle')}
                                onClick={() =>
                                  handleLoginToFacilityPortal(obj.id)
                                }
                                title={t('organizations.loginPortalTitle')}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  marginLeft: '8px',
                                }}>
                                <span
                                  className='material-icons'
                                  style={{ color: '#1976D2' }}>
                                  open_in_new
                                </span>
                              </button>
                            </>
                          )}{' '}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                ) : (
                  <TableBody>
                    <TableRow>
                      <TableCell>{t('organizations.noData')}</TableCell>
                    </TableRow>
                  </TableBody>
                )}
                <TableFooter>
                  <TableRow align='center'>
                    <TablePagination
                      rowsPerPageOptions={[
                        5,
                        10,
                        25,
                        { label: t('common.labels.all'), value: -1 },
                      ]}
                      colSpan={7}
                      count={organizations.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      SelectProps={{
                        inputProps: { roboto: 'rows per page' },
                        native: true,
                      }}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleRowsPerPage}
                      ActionsComponent={TablePaginationActions}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
